import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { useNavigate } from '@/components/Link/Link';
import {
  PageLayout,
  PageLayoutContent,
  PageLayoutLeftPart,
  PageLayoutLeftPartContent,
  PageLayoutSimpleHeader,
} from '@/components/PageLayout';
import config from '@/config';
import Button from '@/design_system/Button';
import { FileViewer } from '@/design_system/FileViewer/FileViewer';
import { Footer, FooterButtons } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconArrow from '@/icons/Arrow.svg';
import { useSendDraftRequest } from '@/models/request';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import { SignatureDialog } from '../../components/SignatureDialog';
import { useRequestContext } from '../../contexts/RequestContext';

import './DropoffSignature.css';

const { element } = createBEMClasses('dropoff-signature');

export const DropoffSignature = ({ onBack }: { onBack: () => void }) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const navigate = useNavigate();
  const { request } = useRequestContext();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [apiError, setApiError] = useState('');

  const goToRequests = ({ skipRouterBlocker }: { skipRouterBlocker: boolean }) =>
    navigate('/requests', { state: { skipRouterBlocker }, replace: true });
  const goToRequest = ({ skipRouterBlocker }: { skipRouterBlocker: boolean }) =>
    navigate(`/requests/${request.id}`, { state: { skipRouterBlocker }, replace: true });

  const {
    mutateAsync: create,
    isPending: isPendingCreate,
    isSuccess: isSuccessCreate,
  } = useSendDraftRequest(request.id);

  const onSaveAndCreate = (signature: string) => {
    create({ saveForLater: false, signature })
      .then(() => {
        goToRequest({ skipRouterBlocker: true });
      })
      .catch((err: any) => {
        console.error(err);
        setApiError(
          (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
        );
      });
  };

  return (
    <PageLayout>
      <PageLayoutSimpleHeader
        title={t({ id: 'requests.new.title', message: 'New request' })}
        subtitle={t({
          id: 'requests.new.drop-off.subtitle',
          message: 'Step 2/2: Drop-off signature',
        })}
        onClose={() => goToRequests({ skipRouterBlocker: !request.draft })}
      />
      <PageLayoutContent>
        <PageLayoutLeftPart>
          <PageLayoutLeftPartContent>
            <Stack gap="1rem" padding={isMobile ? '1rem' : undefined} style={{ flex: 1 }}>
              <Paragraph>
                <Trans id="requests.new.drop-off.description">
                  In order to complete the request creation, please have the client sign the
                  drop-off receipt.
                </Trans>
              </Paragraph>
              <FileViewer
                files={[
                  {
                    id: 'dropoff',
                    type: 'pdf',
                    url: config.apiUrl + `/requests/${request.id}/drop-off-receipt?preview=1`,
                  },
                ]}
              />
            </Stack>
          </PageLayoutLeftPartContent>
          <Footer>
            <FooterButtons>
              <Button
                variant="secondary"
                size="medium"
                onPress={onBack}
                className={element('edit-button')}
              >
                <IconArrow left />
                {t({ id: 'requests.new.drop-off.actions.back', message: 'Edit' })}
              </Button>
              <Button variant="primary" size="medium" onPress={() => setIsDialogOpen(true)}>
                <Trans id="requests.new.drop-off.actions.sign">Sign drop-off</Trans>
              </Button>
            </FooterButtons>
          </Footer>
        </PageLayoutLeftPart>
      </PageLayoutContent>
      <SignatureDialog
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
        title={<Trans id="requests.new.drop-off.dialog.title">Sign drop-off</Trans>}
        submitLabel={
          <Trans id="requests.new.drop-off.dialog.actions.save">Save & create request</Trans>
        }
        onSubmit={onSaveAndCreate}
        isSubmitLoading={isPendingCreate || isSuccessCreate}
        submitError={apiError}
      />
    </PageLayout>
  );
};
